import {
  AdminPage,
  Alert,
  Avatar,
  Card,
  Col,
  DetailDescriptions,
  Row,
  Space,
  Spin,
  Statistic,
  StatusTag,
  Tabs,
  Typography,
} from '@wanjianshi/admin-ui';
import { formatDateTime, formatNumber } from '@wanjianshi/shared-utils';
import { useParams } from 'react-router-dom';
import {
  TeamCreditsPanel,
  TeamMembersPanel,
  TeamProjectsPanel,
  TeamSubscriptionPanel,
} from '@/features/teams/TeamDetailPanels';
import { subscriptionStatusLabels, type TeamDetail } from '@/features/teams/teamDetail';
import { useAdminRecord } from '@/hooks/useAdminResource';

function subscriptionTag(value?: string | null) {
  const normalized = String(value ?? '').toLowerCase();
  return <StatusTag value={normalized} label={subscriptionStatusLabels[normalized] ?? '未知状态'} />;
}

export function TeamDetailPage() {
  const { teamId } = useParams();
  const resource = useAdminRecord<TeamDetail>(teamId ? `/admin/teams/${teamId}` : null);
  const team = resource.data;

  if (!teamId) {
    return <AdminPage title="团队详情"><Alert showIcon type="error" message="团队 ID 无效" /></AdminPage>;
  }

  return (
    <AdminPage
      title={team?.name || `团队 #${teamId}`}
      description="统一查看团队成员、会员套餐、积分流水和团队项目"
      extra={team ? (
        <Space>
          {subscriptionTag(team.subscription_status)}
          <StatusTag
            value={team.status}
            label={Number(team.status) === 0 ? '团队正常' : '团队停用'}
            color={Number(team.status) === 0 ? 'success' : 'default'}
          />
        </Space>
      ) : undefined}
    >
      {resource.error ? (
        <Alert
          showIcon
          type="error"
          message={resource.error}
          action={<a onClick={() => void resource.reload()}>重试</a>}
        />
      ) : null}

      {!team && resource.loading ? (
        <div className="team-detail-loading"><Spin size="large" /></div>
      ) : null}

      {team ? (
        <>
          <Card className="team-detail-overview-card">
            <div className="team-detail-overview-heading">
              <Space size={14} align="start">
                <Avatar shape="square" size={56} src={team.avatar || undefined}>{team.name.slice(0, 1)}</Avatar>
                <div>
                  <Typography.Title level={4}>{team.name}</Typography.Title>
                  <Typography.Text type="secondary">{team.description || '暂无团队描述'}</Typography.Text>
                </div>
              </Space>
              <Typography.Text type="secondary">团队 ID：{team.id}</Typography.Text>
            </div>

            <Row gutter={[16, 16]} className="team-detail-metrics">
              <Col xs={12} md={6}><Statistic title="当前成员" value={team.member_count} suffix={`/ ${formatNumber(team.max_members)} 席`} /></Col>
              <Col xs={12} md={6}><Statistic title="团队项目" value={team.project_count} /></Col>
              <Col xs={12} md={6}><Statistic title="可用积分" value={team.stored_credits} formatter={value => formatNumber(String(value))} /></Col>
              <Col xs={12} md={6}><Statistic title="累计已用积分" value={team.used_credits} formatter={value => formatNumber(String(value))} /></Col>
            </Row>

            <DetailDescriptions
              className="team-detail-base-info"
              column={{ xs: 1, sm: 2, lg: 4 }}
              items={[
                { key: 'founder', label: '所属人', children: team.founder_nickname || team.founder_username || '—' },
                { key: 'founder_id', label: '所属人用户 ID', children: String(team.user_id) },
                { key: 'founder_mobile', label: '所属人手机号', children: team.founder_mobile_masked || '—' },
                { key: 'founder_email', label: '所属人邮箱', children: team.founder_email_masked || '—' },
                { key: 'subscription', label: '会员套餐状态', children: subscriptionTag(team.subscription_status) },
                { key: 'subscription_id', label: '当前订阅记录 ID', children: team.subscription_id ? String(team.subscription_id) : '—' },
                { key: 'total_credits', label: '团队总积分', children: formatNumber(team.total_credits) },
                { key: 'frozen_credits', label: '成员冻结积分', children: formatNumber(team.frozen_credits) },
                { key: 'create_time', label: '创建时间', children: formatDateTime(team.create_time) },
                { key: 'update_time', label: '更新时间', children: formatDateTime(team.update_time) },
              ]}
            />
          </Card>

          <Card className="team-detail-tabs-card">
            <Tabs
              destroyOnHidden
              items={[
                { key: 'members', label: `团队成员（${formatNumber(team.member_count)}）`, children: <TeamMembersPanel teamId={teamId} /> },
                { key: 'subscription', label: '会员套餐', children: <TeamSubscriptionPanel teamId={teamId} /> },
                { key: 'credits', label: '积分与流水', children: <TeamCreditsPanel teamId={teamId} /> },
                { key: 'projects', label: `团队项目（${formatNumber(team.project_count)}）`, children: <TeamProjectsPanel teamId={teamId} /> },
              ]}
            />
          </Card>
        </>
      ) : null}
    </AdminPage>
  );
}
